// ---------------------------------------------------------------------------
// Combobox keyboard utilities
// ---------------------------------------------------------------------------
// Reusable keyboard navigation for combobox, listbox, and menu patterns.
// Based on the W3C APG Select-Only Combobox pattern:
// https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/
// ---------------------------------------------------------------------------

export const ComboboxAction = {
  ClosePopup: "ClosePopup",
  CommitAndClose: "CommitAndClose",
  MoveFirst: "MoveFirst",
  MoveLast: "MoveLast",
  MoveNext: "MoveNext",
  MovePrevious: "MovePrevious",
  OpenPopup: "OpenPopup",
  TypeCharacter: "TypeCharacter",
} as const

export type ComboboxAction =
  (typeof ComboboxAction)[keyof typeof ComboboxAction]

const keyActionsWhenClosed: Record<string, ComboboxAction> = {
  " ": ComboboxAction.OpenPopup,
  ArrowDown: ComboboxAction.MoveFirst,
  ArrowUp: ComboboxAction.MoveFirst,
  End: ComboboxAction.MoveLast,
  Enter: ComboboxAction.OpenPopup,
  Home: ComboboxAction.MoveFirst,
}

const keyActionsWhenOpen: Record<string, ComboboxAction> = {
  " ": ComboboxAction.CommitAndClose,
  ArrowDown: ComboboxAction.MoveNext,
  ArrowUp: ComboboxAction.MovePrevious,
  End: ComboboxAction.MoveLast,
  Enter: ComboboxAction.CommitAndClose,
  Escape: ComboboxAction.ClosePopup,
  Home: ComboboxAction.MoveFirst,
}

/**
 * Returns the combobox action for a keyboard event, based on whether the
 * popup is currently open or closed. Returns `undefined` when the key has
 * no mapped behavior.
 */
export function getComboboxActionFromKey(
  event: Pick<KeyboardEvent, "altKey" | "ctrlKey" | "key" | "metaKey">,
  popupIsOpen: boolean
): ComboboxAction | undefined {
  const { altKey, ctrlKey, key, metaKey } = event

  if (key.length === 1 && key !== " " && !altKey && !ctrlKey && !metaKey) {
    return ComboboxAction.TypeCharacter
  }

  const actionMap = popupIsOpen ? keyActionsWhenOpen : keyActionsWhenClosed
  return actionMap[key]
}

function clamp(value: number, max: number): number {
  const min = 0
  if (value < min) return min
  if (value > max) return max
  return value
}

/**
 * Calculates the next active option index given the current index and the
 * navigation action. Clamps the result within bounds.
 * Skips disabled options by searching forward or backward from the target index.
 * Returns -1 if all options are disabled.
 */
export function getUpdatedIndex(
  currentIndex: number,
  action: ComboboxAction,
  options: Array<{ disabled?: boolean }>
): number {
  const maxIndex = options.length - 1

  // Calculate raw target index based on action
  let targetIndex: number
  switch (action) {
    case ComboboxAction.MoveFirst:
      targetIndex = 0
      break
    case ComboboxAction.MoveLast:
      targetIndex = maxIndex
      break
    case ComboboxAction.MoveNext:
      targetIndex = clamp(currentIndex + 1, maxIndex)
      break
    case ComboboxAction.MovePrevious:
      targetIndex = clamp(currentIndex - 1, maxIndex)
      break
    default:
      return currentIndex
  }

  // If target is enabled, return it
  if (!options[targetIndex]?.disabled) {
    return targetIndex
  }

  // Determine search direction based on action
  const searchForward =
    action === ComboboxAction.MoveNext || action === ComboboxAction.MoveFirst

  // Search for next enabled option
  for (let i = 1; i < options.length; i++) {
    const offset = searchForward ? i : -i
    const candidateIndex =
      (targetIndex + offset + options.length) % options.length

    if (!options[candidateIndex]?.disabled) {
      return candidateIndex
    }
  }

  // All options are disabled
  return -1
}
